/**
 * @file App.tsx
 * @module app/App
 * @description Root application component with routing and providers.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Suspense, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import sound from "../_admin/assets/media/new_order_bell.wav";
import { I18nProvider } from "../_admin/i18n/i18nProvider";
import { LayoutProvider, LayoutSplashScreen } from "../_admin/layout/core";
import { MasterInit } from "../_admin/layout/MasterInit";
import PendingDepositModal from "./modals/pending-deposit-modal";
import StockUpdateAlertModal, {
  StockUpdateAlertItem,
} from "./modals/stock-update-alert-modal";
import { Notice } from "./components/Notice";
import { AuthInit, useAuth } from "./modules/auth";
import { useSocket } from "./modules/socket/core/Socket";
import { useWarehouse } from "./context/WarehouseContext";
import InwardSlaBell from "./modules/inward/components/inward-sla-bell";
import { usePostHog } from "@posthog/react";
import { initializeReleaseHighlighterSystem } from "./utils/releaseHighlighter";
import { POSTHOG_ENABLED_CACHE_KEY } from "../utils/constants";
import APICallService from "../api/apiCallService";
import { routePlanning } from "../api/apiEndPoints";
import {
  fetchConfigurationSettingByKey,
  getValidatedBooleanSettingValue,
} from "../utils";
import { CONFIGURATION_SETTING_KEYS } from "../utils/configurationSettingKeys";
import { notifyByKey } from "./modules/_common/utils/sweet-alert";

function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathname: any = useRef();
}
const App = () => {
  const { pathname, search, hash } = useLocation();
  const prevPathname: any = useRef();
  // Initialize scroll position from localStorage or set to 0
  const initialScrollPosition: any = localStorage.getItem("scrollPosition")
    ? localStorage.getItem("scrollPosition")
    : 0 || 0;
  // Set the scroll position in the component state
  const [scrollY, setScrollY] = useState<any>(parseInt(initialScrollPosition));
  const { socket } = useSocket();
  const { currentUser } = useAuth();
  const { selectedWarehouse, warehouseList } = useWarehouse();
  const posthog = usePostHog();
  const [isPosthogEnabled, setIsPosthogEnabled] = useState(true);

  const isSuperAdmin = !!currentUser && currentUser?.userType == 1;
  const isFinanceManager =
    !!currentUser &&
    currentUser?.userType == 3 &&
    currentUser?.roleAndPermission?.roleType == "R0003";
  const isWarehouseManager =
    !!currentUser &&
    !!currentUser?.roleAndPermission?.isWarehouseManagerRole;
  const canPlayAlertSound =
    isSuperAdmin || isFinanceManager || isWarehouseManager;

  const [pendingDrivers, setPendingDrivers] = useState<any>([]);
  const [stockUpdateAlerts, setStockUpdateAlerts] = useState<
    StockUpdateAlertItem[]
  >([]);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playCountRef = useRef<number>(0);
  const endedHandlerRef = useRef<(() => void) | null>(null);
  const playPromiseRef = useRef<Promise<void> | null>(null); // Added this reference
  const playGenerationRef = useRef<number>(0);
  const playPendingDepositSound = () => {
    const audio = audioRef.current;
    if (!audio) return;

    // New generation invalidates in-flight ended handlers / stale pause callbacks
    const generation = ++playGenerationRef.current;

    if (endedHandlerRef.current) {
      audio.removeEventListener("ended", endedHandlerRef.current);
      endedHandlerRef.current = null;
    }
    playCountRef.current = 0;
    playPromiseRef.current = null;

    audio.pause();
    audio.currentTime = 0;

    const handleEnded = () => {
      if (playGenerationRef.current !== generation) return;
      playCountRef.current += 1;
      if (playCountRef.current < 2) {
        audio.currentTime = 0;
        playPromiseRef.current = audio.play();
        playPromiseRef.current?.catch((error) => {
          console.error("Failed to replay pending deposit sound:", error);
        });
      } else {
        audio.removeEventListener("ended", handleEnded);
        if (endedHandlerRef.current === handleEnded) {
          endedHandlerRef.current = null;
        }
      }
    };

    endedHandlerRef.current = handleEnded;
    audio.addEventListener("ended", handleEnded);

    audio.loop = false;
    playPromiseRef.current = audio.play();
    playPromiseRef.current?.catch((error) => {
      console.error("Failed to play pending deposit sound:", error);
    });
  };
  const stopPendingDepositSound = () => {
    const audio = audioRef.current;
    if (!audio) return;

    playGenerationRef.current += 1;

    if (endedHandlerRef.current) {
      audio.removeEventListener("ended", endedHandlerRef.current);
      endedHandlerRef.current = null;
    }
    playCountRef.current = 0;
    playPromiseRef.current = null;

    audio.pause();
    audio.currentTime = 0;
  };
  const handleEnableAudio = () => {
    const audio = audioRef.current;
    if (!audio || audioEnabled) return;

    audio.muted = false;

    const playPromise = audio.play();

    if (playPromise) {
      playPromise
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          setAudioEnabled(true);
        })
        .catch((error) => {
          console.error("Failed to enable audio:", error);
        });
    } else {
      setAudioEnabled(true);
    }
  };

  // Browsers only allow sound after a user interaction: enable it on the first click / key press
  // anywhere, without blocking that click (a full-page overlay swallowed it, and kept blocking
  // every click when the browser refused to play).
  useEffect(() => {
    if (!canPlayAlertSound || audioEnabled) return;
    const enable = () => handleEnableAudio();
    document.addEventListener("pointerdown", enable, true);
    document.addEventListener("keydown", enable, true);
    return () => {
      document.removeEventListener("pointerdown", enable, true);
      document.removeEventListener("keydown", enable, true);
    };
  });

  useEffect(() => {
    if (!socket) return;

    const handlePendingDeposit = (payload: any) => {
      if (!isFinanceManager) return;
      const list = payload?.drivers || [];

      let filteredList = list;
      if (selectedWarehouse) {
        filteredList = list.filter((driver: any) => {
          if (!Array.isArray(driver.warehouses)) return false;
          return driver.warehouses.some(
            (wh: any) => wh?.reference === selectedWarehouse._id
          );
        });
      }

      if (filteredList.length) {
        setPendingDrivers(filteredList);
        if (audioEnabled) {
          playPendingDepositSound();
        }
      } else {
        setPendingDrivers([]);
        stopPendingDepositSound();
      }
    };

    const handleStockUpdateAlert = (payload: any) => {
      if (!canPlayAlertSound) return;
      // Superadmins hear all warehouses; others only the selected warehouse
      if (
        !isSuperAdmin &&
        selectedWarehouse?._id &&
        payload?.warehouseId &&
        String(payload.warehouseId) !== String(selectedWarehouse._id)
      ) {
        return;
      }

      const warehouseName =
        payload?.warehouseName ||
        warehouseList.find(
          (warehouse) =>
            String(warehouse._id) === String(payload?.warehouseId)
        )?.label ||
        selectedWarehouse?.label ||
        "";

      setStockUpdateAlerts((prev) => [
        ...prev,
        {
          id: `${payload?.emittedAt || Date.now()}-${payload?.orderId || prev.length}`,
          warehouseName,
          driverName: payload?.driverName || "",
          message: payload?.message || "",
        },
      ]);

      if (audioEnabled) {
        playPendingDepositSound();
      }
    };

    const handleSwalNotification = (payload: {
      settingKey?: string;
      icon?: string;
      title?: string;
      message?: string;
      metadata?: {
        routeId?: string;
        vehicleId?: string;
        orderId?: string;
      };
    }) => {
      if (!payload?.settingKey) {
        return;
      }

      const callback =
        payload.settingKey ===
        CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_LAST_ORDER_DELIVERED
          ? async () => {
              const response = await new APICallService(
                routePlanning.acknowledgeLastOrderDelivered,
                payload.metadata || {},
              ).callAPI();
              if (!response || response === 0 || response.status === "error") {
                throw new Error(
                  response?.message || "Failed to acknowledge notification",
                );
              }
              return response;
            }
          : undefined;

        const notificationTitle =
          payload.title ||
          (payload.settingKey ===
          CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_LAST_ORDER_DELIVERED
            ? "Last order delivered"
            : "");

      void notifyByKey(
        payload.settingKey,
          [payload.icon || "success", notificationTitle, payload.message || ""],
        true,
        callback,
      );
    };

    socket.on("onPendingDeposit", handlePendingDeposit);
    socket.on("onStockUpdateAlert", handleStockUpdateAlert);
    socket.on("onNotification", handleSwalNotification);
    return () => {
      socket.off("onPendingDeposit", handlePendingDeposit);
      socket.off("onStockUpdateAlert", handleStockUpdateAlert);
      socket.off("onNotification", handleSwalNotification);
    };
  }, [
    socket,
    selectedWarehouse,
    warehouseList,
    audioEnabled,
    isFinanceManager,
    canPlayAlertSound,
  ]);

  useEffect(() => {
    if (audioEnabled && pendingDrivers.length) {
      playPendingDepositSound();
    }
  }, [audioEnabled]);

  useEffect(() => {
    // Guard prevents state updates if the component unmounts before async work finishes.
    let isMounted = true;

    /**
     * Loads PostHog settings from the configuration API.
     * @returns {Promise<void>} Promise resolved after local state synchronization.
     */
    const loadConfigurationSettings = async () => {
      if (!currentUser) {
        if (isMounted) {
          setIsPosthogEnabled(false);
        }
        return;
      }

      try {
        const posthogSetting = await fetchConfigurationSettingByKey(
          CONFIGURATION_SETTING_KEYS.KEY__ENABLE_POSTHOG,
        );

        const enabled = getValidatedBooleanSettingValue(
          posthogSetting?.value ?? posthogSetting?.defaultValue,
          true,
        );

        if (isMounted) {
          setIsPosthogEnabled(enabled);
        }

        localStorage.setItem(POSTHOG_ENABLED_CACHE_KEY, String(enabled));
      } catch (_error) {
        if (isMounted) {
          setIsPosthogEnabled(true);
        }
      }
    };

    loadConfigurationSettings();

    return () => {
      // Mark effect as inactive so late async responses do not call setState.
      isMounted = false;
    };
  }, [currentUser]);

  useEffect(() => {
    if (!posthog || isPosthogEnabled) return;
    posthog.reset();
  }, [posthog, isPosthogEnabled]);

  // Identify user in PostHog when currentUser changes
  useEffect(() => {
    if (!posthog || !isPosthogEnabled) return;

    if (currentUser) {
      posthog.identify(currentUser._id || currentUser.email, {
        email: currentUser.email,
        name: currentUser.name,
        phone: currentUser.phone,
        phoneCountry: currentUser.phoneCountry,
        userType: currentUser.userType,
        roles: Array.isArray(currentUser.role)
          ? currentUser.role.map((r: any) => r.type || r)
          : currentUser.role,
        warehouse: currentUser.warehouse?.name || currentUser.warehouse,
      });
    } else {
      posthog.reset();
    }
  }, [posthog, currentUser, isPosthogEnabled]);

  // Global PostHog click tracking for buttons, links, table rows, and modals
  useEffect(() => {
    if (!posthog || !isPosthogEnabled) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const clickable = target.closest(
        "button, a, tr, [role='button'], [data-analytics-event]"
      ) as HTMLElement | null;
      if (!clickable) return;

      const eventName = clickable.getAttribute("data-analytics-event") || "ui_click";

      const inModal = !!target.closest(".modal, .modal-content, [role='dialog']");
      const elementText = clickable.textContent?.trim().slice(0, 80) || "";
      const elementTag = clickable.tagName.toLowerCase();
      const elementId = clickable.id || undefined;
      const elementClasses = clickable.className || undefined;

      posthog.capture(eventName, {
        click_target_tag: elementTag,
        click_target_text: elementText,
        click_target_id: elementId,
        click_target_classes: elementClasses,
        click_in_modal: inModal,
        click_url_path: window.location.pathname,
      });
    };

    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [posthog, isPosthogEnabled]);

  useEffect(() => {
    if (!currentUser) return;
    return initializeReleaseHighlighterSystem(pathname, search, hash);
  }, [currentUser, pathname, search, hash]);

  return (
    <>
      {!!currentUser && (
        <>
          {isFinanceManager && pendingDrivers.length > 0 && (
            <PendingDepositModal
              show={pendingDrivers.length > 0}
              drivers={pendingDrivers}
              onClose={() => {
                stopPendingDepositSound();
                setPendingDrivers([]);
              }}
            />
          )}
          {canPlayAlertSound && stockUpdateAlerts.length > 0 && (
            <StockUpdateAlertModal
              show={stockUpdateAlerts.length > 0}
              alerts={stockUpdateAlerts}
              onClose={() => {
                if (!pendingDrivers.length) {
                  stopPendingDepositSound();
                }
                setStockUpdateAlerts([]);
              }}
            />
          )}
          <audio ref={audioRef} src={sound} muted={!audioEnabled} />
          {/* Red bell on every page while a pending action is past its SLA */}
          <InwardSlaBell />
        </>
      )}
      <Suspense fallback={<LayoutSplashScreen />}>
        <I18nProvider>
          <LayoutProvider>
            <AuthInit>
              <Outlet />
              {/* <ScrollToTop /> */}
              <MasterInit />
              {currentUser && <Notice />}
            </AuthInit>
          </LayoutProvider>
        </I18nProvider>
      </Suspense>
    </>
  );
};
export { App };